import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Rocket, Target, CalendarCheck, Lightning, Fire, CheckCircle, ArrowRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const PHASE = {
  j30: { label: "J-30 · Mise en route", color: "#042A65", bg: "#042A65" },
  j15: { label: "J-15 · Accélération", color: "#FFB800", bg: "#B5780A" },
  j7: { label: "J-7 · Sprint final", color: "#FF4B0A", bg: "#C0452A" },
  passed: { label: "Examen passé", color: "#595959", bg: "#595959" },
};

export default function Sprint() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/sprint/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setExamDate = async () => {
    if (!date) { toast.error("Choisissez une date."); return; }
    setSaving(true);
    try {
      await api.put("/auth/me", { exam_date: date });
      await refreshUser();
      await load();
      toast.success("Date d'examen enregistrée !");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;

  const ph = PHASE[data.phase] || PHASE.j30;

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Mode spécial</span>
        <h1 className="mt-2 flex items-center gap-2 font-display text-3xl font-extrabold sm:text-4xl"><Rocket weight="fill" className="text-terracotta" /> Dernière ligne droite</h1>
        <p className="mt-1 text-ink/65">Révisions prioritaires, examens blancs et conseils pour les dernières semaines avant l'examen.</p>
      </div>

      {data.days_left === null ? (
        <div className="rounded-2xl border border-border bg-white p-8 text-center">
          <CalendarCheck weight="duotone" className="mx-auto text-terracotta" size={36} />
          <h2 className="mt-3 font-display text-xl font-bold">Quand passez-vous votre examen ?</h2>
          <p className="mt-1 text-sm text-muted-foreground">Indiquez la date pour activer votre plan de révision personnalisé.</p>
          <div className="mx-auto mt-5 flex max-w-sm flex-col gap-3 sm:flex-row">
            <Input data-testid="exam-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl" />
            <Button data-testid="set-exam-date" onClick={setExamDate} disabled={saving} className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]">
              {saving ? "…" : "Activer"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Countdown + readiness */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl p-6 text-white lg:col-span-1" style={{ background: ph.bg }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{ph.label}</span>
                <Fire weight="fill" className="text-white" size={22} />
              </div>
              <div className="mt-3 font-display text-5xl font-extrabold">{data.days_left >= 0 ? data.days_left : "—"}</div>
              <div className="text-white/80">{data.days_left >= 0 ? "jours restants" : "examen passé"}</div>
              <div className="mt-3 text-sm text-white/70">{new Date(data.exam_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
            </div>

            <div className="rounded-2xl border border-border bg-white p-6 lg:col-span-2">
              <div className="flex items-center gap-2"><Target weight="fill" className="text-forest" size={22} /><h2 className="font-display text-lg font-bold">Niveau de préparation estimé</h2></div>
              <div className="mt-4 flex items-end gap-4">
                <div className="font-display text-4xl font-extrabold" style={{ color: ph.color }}>{data.readiness}%</div>
                <div className="mb-1 flex-1">
                  <div className="h-3 overflow-hidden rounded-full bg-sand">
                    <div className="h-full rounded-full transition-all" style={{ width: `${data.readiness}%`, background: ph.color }} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">Basé sur vos {data.quizzes_done} quiz · score moyen {data.avg_score}%</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-terracotta/5 px-4 py-3 text-sm">
                <Lightning weight="fill" className="text-terracotta" /> <span><span className="font-semibold">Objectif du jour :</span> {data.daily_goal}</span>
              </div>
            </div>
          </div>

          {/* Priorities */}
          <div className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-display text-lg font-bold">Matières prioritaires</h2>
            <p className="text-sm text-muted-foreground">Concentrez vos efforts ici en priorité.</p>
            <div className="mt-4 space-y-2">
              {data.priorities.length ? data.priorities.map((p, i) => (
                <Link key={i} to={`/app/cours/${data.exam?.id}/${encodeURIComponent(p.subject)}`} data-testid={`priority-${i}`}
                  className="group flex items-center gap-3 rounded-xl bg-cream px-4 py-3 transition hover:bg-sand">
                  <div className={`grid h-8 w-8 place-items-center rounded-lg text-xs font-bold text-white ${p.avg === null ? "bg-muted-foreground" : p.avg < 50 ? "bg-destructive" : "bg-gold"}`}>{i + 1}</div>
                  <div className="flex-1">
                    <div className="font-semibold">{p.subject}</div>
                    <div className="text-xs text-muted-foreground">{p.avg === null ? "Non encore travaillée" : `Moyenne ${p.avg}% · ${p.attempts} quiz`}</div>
                  </div>
                  {p.avg !== null && p.avg < 50 && <Warning weight="fill" className="text-destructive" />}
                  <ArrowRight className="text-muted-foreground transition group-hover:translate-x-1 group-hover:text-terracotta" />
                </Link>
              )) : <p className="py-4 text-center text-sm text-muted-foreground">Aucune donnée — commencez par quelques quiz.</p>}
            </div>
          </div>

          {/* Tips */}
          <div className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-display text-lg font-bold">Conseils pour cette phase</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {data.tips.map((t, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl bg-cream px-4 py-3 text-sm"><CheckCircle weight="fill" className="mt-0.5 shrink-0 text-forest" /> {t}</li>
              ))}
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/app/tuteur" className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-forest/90"><Rocket weight="fill" /> Planning avec le Prof IA</Link>
              <Link to="/app/cours" className="inline-flex items-center gap-2 rounded-full border-2 border-terracotta px-5 py-2.5 text-sm font-semibold text-terracotta hover:bg-terracotta hover:text-white">Réviser maintenant</Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
